import cx from 'classnames';
import { useRef, type PointerEvent, type ReactNode } from 'react';

export interface ControlSliderProps {
  /** Whether the slider pill is vertical (tall, for lift) or horizontal (wide, for joints) */
  orientation: 'horizontal' | 'vertical';
  /**
   * Current value position as a fraction from 0 (min) to 1 (max).
   * Drives where the track indicator is placed inside the pill.
   */
  valuePercent: number;
  /** Text displayed in the center of the pill */
  valueLabel: string;
  /** Button for decreasing the value (− / down / left). Use variant="Flat". */
  decrementButton: ReactNode;
  /** Button for increasing the value (+ / up / right). Use variant="Flat". */
  incrementButton: ReactNode;
  /**
   * Opt-in drag-to-set. When provided, pressing and dragging anywhere on the
   * pill track (outside the ± buttons) reports the pointer position as a
   * fraction from 0 (min) to 1 (max). Consumers that omit this keep the
   * button-only behavior unchanged. Drag only reports the value — it does not
   * actuate anything itself.
   */
  onScrub?: (valuePercent: number) => void;
  className?: string;
  'data-testid'?: string;
}

function clamp01(value: number): number {
  if (value < 0) return 0;
  if (value > 1) return 1;

  return value;
}

// Vertical pill geometry
const V_HEIGHT_PX = 247;
const V_TRACK_TOP_PX = 45; // space occupied by the + button at top
const V_TRACK_BOTTOM_PX = 44; // space occupied by the - button at bottom
const V_TRACK_RANGE_PX = V_HEIGHT_PX - V_TRACK_TOP_PX - V_TRACK_BOTTOM_PX;

// Horizontal pill geometry — buttons are ~44px wide including the gap from the edge
const H_BUTTON_AREA_PX = 44;

const BUTTON_CIRCLE_BASE = cx('tw-rounded-full', 'tw-bg-fill-secondary');

export function ControlSlider({
  orientation,
  valuePercent,
  valueLabel,
  decrementButton,
  incrementButton,
  onScrub,
  className,
  'data-testid': dataTestId,
}: ControlSliderProps) {
  const pillRef = useRef<HTMLDivElement>(null);
  // Tracks whether an active drag started on the track (vs. a press that began
  // on a ± button, which we ignore). Using a ref rather than the Pointer
  // Capture API avoids releasing a capture we never took and keeps the
  // handlers robust in environments that don't implement it.
  const isScrubbingRef = useRef(false);

  // Map a pointer position to a 0..1 fraction along the track, excluding the
  // ± button areas at each end so the draggable range lines up with the
  // rendered track indicator.
  const fractionFromPointer = (event: PointerEvent): number | null => {
    const el = pillRef.current;

    if (!el) return null;

    const rect = el.getBoundingClientRect();

    if (orientation === 'horizontal') {
      const trackLen = rect.width - H_BUTTON_AREA_PX * 2;

      if (trackLen <= 0) return null;

      return clamp01(
        (event.clientX - (rect.left + H_BUTTON_AREA_PX)) / trackLen,
      );
    }

    // Vertical: top edge of the track is max (1), bottom is min (0).
    if (V_TRACK_RANGE_PX <= 0) return null;

    const fromTop =
      (event.clientY - (rect.top + V_TRACK_TOP_PX)) / V_TRACK_RANGE_PX;

    return clamp01(1 - fromTop);
  };

  const handlePointerDown = (event: PointerEvent<HTMLDivElement>) => {
    if (!onScrub) return;

    // Let the ± buttons keep their own hold-to-step behavior; only bare track
    // presses start a drag.
    if ((event.target as HTMLElement).closest('button')) return;

    const fraction = fractionFromPointer(event);

    if (fraction === null) return;

    // Capture the pointer so the drag keeps tracking even if it leaves the
    // pill, and report the initial press position so a tap (no move) still
    // sets the value.
    event.preventDefault();
    isScrubbingRef.current = true;
    pillRef.current?.setPointerCapture?.(event.pointerId);
    onScrub(fraction);
  };

  const handlePointerMove = (event: PointerEvent<HTMLDivElement>) => {
    if (!onScrub || !isScrubbingRef.current) return;

    const fraction = fractionFromPointer(event);

    if (fraction !== null) onScrub(fraction);
  };

  // Ends an active drag. Wired to pointerup as well as pointercancel and
  // lostpointercapture so an interrupted gesture (system gesture, scroll
  // takeover, capture loss) still clears the scrubbing flag — otherwise it
  // stays set and onScrub keeps firing on plain hover without a new press.
  const endScrub = (event: PointerEvent<HTMLDivElement>) => {
    if (!isScrubbingRef.current) return;

    isScrubbingRef.current = false;
    pillRef.current?.releasePointerCapture?.(event.pointerId);
  };

  // Shared drag wiring applied to whichever pill container renders below.
  // Attached unconditionally: the guards inside handlePointerDown/-Move
  // already prevent scrubbing when onScrub is absent, and the end handlers
  // must survive a consumer momentarily passing onScrub=undefined mid-drag
  // (e.g. a disable blip) — otherwise the scrubbing flag stays set and plain
  // hover would scrub again once onScrub is restored.
  const scrubProps = {
    onPointerDown: handlePointerDown,
    onPointerMove: handlePointerMove,
    onPointerUp: endScrub,
    onPointerCancel: endScrub,
    onLostPointerCapture: endScrub,
  };

  if (orientation === 'vertical') {
    // Track moves top (max) → bottom (min)
    const trackTopPx = V_TRACK_TOP_PX + (1 - valuePercent) * V_TRACK_RANGE_PX;

    return (
      <div
        ref={pillRef}
        data-testid={dataTestId}
        {...scrubProps}
        className={cx(
          'tw-relative',
          'tw-w-[44px]',
          'tw-h-[247px]',
          'tw-rounded-[45px]',
          'tw-bg-fill-primary',
          'tw-shadow-30',
          'tw-overflow-hidden',
          onScrub && 'tw-touch-none',
          className,
        )}
      >
        <div
          className={cx(
            BUTTON_CIRCLE_BASE,
            'tw-absolute',
            'tw-top-[4px]',
            'tw-left-1/2',
            '-tw-translate-x-1/2',
          )}
        >
          {incrementButton}
        </div>

        {/* Track — h-[44px] w-[2px] rotated -90° becomes a 44px-wide horizontal line */}
        <div
          className={cx(
            'tw-absolute',
            'tw-left-0',
            'tw-right-0',
            '-tw-translate-y-1/2',
            'tw-h-[2px]',
            'tw-flex',
            'tw-items-center',
            'tw-justify-center',
          )}
          style={{ top: `${trackTopPx}px` }}
        >
          <div
            className={cx(
              '-tw-rotate-90',
              'tw-flex-none',
              'tw-h-[44px]',
              'tw-w-[2px]',
              'tw-overflow-hidden',
              'tw-relative',
            )}
          >
            <div className="tw-absolute -tw-translate-x-1/2 tw-bg-fill-secondary tw-bottom-0 tw-left-1/2 tw-top-0 tw-w-[2px]" />
            <div className="tw-absolute -tw-translate-x-1/2 tw-bg-label-primary tw-left-1/2 tw-size-[2px] tw-top-0" />
            <div className="tw-absolute -tw-translate-x-1/2 tw-bg-label-primary tw-bottom-0 tw-left-1/2 tw-size-[2px]" />
          </div>
        </div>

        <span
          className={cx(
            'tw-absolute',
            'tw-left-1/2',
            '-tw-translate-x-1/2',
            'tw-top-1/2',
            '-tw-translate-y-1/2',
            'tw-text-15',
            'tw-text-center',
            'tw-w-[80px]',
            'tw-tabular-nums',
            'tw-pointer-events-none',
            'tw-z-10',
          )}
        >
          {valueLabel}
        </span>

        <div
          className={cx(
            BUTTON_CIRCLE_BASE,
            'tw-absolute',
            'tw-bottom-[4px]',
            'tw-left-1/2',
            '-tw-translate-x-1/2',
          )}
        >
          {decrementButton}
        </div>
      </div>
    );
  }

  // Horizontal orientation: - at left, + at right, track moves left ↔ right
  const trackLeft = `calc(${H_BUTTON_AREA_PX}px + ${valuePercent} * (100% - ${H_BUTTON_AREA_PX * 2}px))`;

  return (
    <div
      ref={pillRef}
      data-testid={dataTestId}
      {...scrubProps}
      className={cx(
        'tw-relative',
        'tw-flex-1',
        'tw-h-[44px]',
        'tw-rounded-full',
        'tw-bg-fill-primary',
        'tw-shadow-30',
        'tw-overflow-hidden',
        onScrub && 'tw-touch-none',
        className,
      )}
    >
      <div
        className={cx(
          BUTTON_CIRCLE_BASE,
          'tw-absolute',
          'tw-left-[4px]',
          'tw-top-1/2',
          '-tw-translate-y-1/2',
        )}
      >
        {decrementButton}
      </div>

      {/* Track — vertical 2px line that moves horizontally */}
      <div
        className={cx(
          'tw-absolute',
          'tw-top-0',
          'tw-bottom-0',
          'tw-w-[2px]',
          '-tw-translate-x-1/2',
          'tw-pointer-events-none',
        )}
        style={{ left: trackLeft }}
      >
        <div className="tw-absolute tw-inset-0 tw-bg-fill-secondary" />
        <div className="tw-absolute tw-top-0 tw-inset-x-0 tw-size-[2px] tw-bg-label-primary" />
        <div className="tw-absolute tw-bottom-0 tw-inset-x-0 tw-size-[2px] tw-bg-label-primary" />
      </div>

      <span
        className={cx(
          'tw-absolute',
          'tw-left-1/2',
          '-tw-translate-x-1/2',
          'tw-top-1/2',
          '-tw-translate-y-1/2',
          'tw-text-15',
          'tw-text-center',
          'tw-tabular-nums',
          'tw-pointer-events-none',
          'tw-z-10',
        )}
      >
        {valueLabel}
      </span>

      <div
        className={cx(
          BUTTON_CIRCLE_BASE,
          'tw-absolute',
          'tw-right-[4px]',
          'tw-top-1/2',
          '-tw-translate-y-1/2',
        )}
      >
        {incrementButton}
      </div>
    </div>
  );
}
